/**
 * Centralized Axios Instance with Auth Interceptors
 * Handles token management, 401 errors, and session persistence
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple redirects
let isRedirecting = false;

// Request interceptor - add auth token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors globally
api.interceptors.response.use(
  (response) => {
    // Success - return response
    return response;
  },
  (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Prevent multiple redirects
      if (!isRedirecting) {
        isRedirecting = true;
        
        console.warn('Session expired or unauthorized. Clearing auth state...');
        
        // Get login_source before clearing for redirect
        const loginSource = localStorage.getItem('login_source') || '/';
        
        // Clear all auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('school_code');
        localStorage.removeItem('login_source');
        sessionStorage.removeItem('auth_retry_count');
        
        // Small delay to allow cleanup
        setTimeout(() => {
          isRedirecting = false;
          
          // Redirect to where user originally logged in from
          window.location.href = loginSource;
        }, 100);
      }
    }
    
    // Handle 403 Forbidden (permission denied)
    if (error.response?.status === 403) {
      console.warn('Access forbidden:', error.response?.data?.detail);
    }
    
    // Handle network errors
    if (!error.response) {
      console.error('Network error or timeout:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Helper function to get auth header (for components still using raw axios)
export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Export the configured axios instance
export default api;
